import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { ICalenderParam, IEvent, IMemberEvent, ISession } from "../types"
import api from "services/api"
import { IPresubscription } from "pages/cart/types"

export function useMemberEvents(
  memberId: string,
  params?: any,
  config?: UseQueryOptions<IEvent[]>
) {
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "events",
      params,
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useSubscriptionsHistory(
  memberId: string,
  params?: { start_date?: string; end_date?: string },
  config?: UseQueryOptions<IEvent[]>
) {
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "events-history",
      params,
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useWaitingList(
  memberId: string,
  config?: UseQueryOptions<IEvent[]>
) {
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "waiting-events",
    ],
    config
  )
}

export function usePresubscriptions(
  memberId: string,
  params?: any,
  config?: UseQueryOptions<IPresubscription[]>
) {
  return useQuery<IPresubscription[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "presubscriptions",
      params,
    ],
    config
  )
}

export function useExtSessions(
  params?: any,
  config?: UseQueryOptions<ISession[]>
) {
  return useQuery<ISession[]>(
    ["tactical", "production", "sessions", "external", params],
    {
      ...config,
    }
  )
}

export function useExtCurrentSession(config?: UseQueryOptions<ISession>) {
  return useQuery<ISession>(
    ["tactical", "production", "sessions", "external", "current"],
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...config,
    }
  )
}

//   export function useMemberEventsFilters() {
//     return useQuery([
//       "operation",
//       "administration",
//       "members",
//       "portal",
//       "filters",
//     ])
//   }

export function useExportMemberEventsIcal(
  memberId: string,
  config?: UseMutationOptions<any>
) {
  return useMutation<any>(
    async () => {
      const response = await api.get(
        `operation/administration/members/${memberId}/generate-calendar-portal`
      )

      const disposition = response.headers["content-disposition"]
      let filename = "Events.ical"
      if (disposition && disposition.includes("attachment")) {
        const filenameMatch = disposition.match(/filename="(.+)"/)
        if (filenameMatch?.length === 2) {
          filename = filenameMatch[1]
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    {
      ...config,
    }
  )
}

export function useDeletePresubscription(
  memberId: string,
  config?: UseMutationOptions<
    unknown,
    unknown,
    { presubscription_id: string; apply_to_similar_events: boolean }
  >
) {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    unknown,
    { presubscription_id: string; apply_to_similar_events: boolean }
  >(
    (data) =>
      api.delete(
        `operation/administration/members/portal/${memberId}/presubscriptions`,
        { data }
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "presubscriptions",
        ])
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "to_subscribe_events",
        ])
      },
    }
  )
}

export function useToggleAbsence(
  memberId: string,
  config?: UseMutationOptions<
    unknown,
    unknown,
    { eventId: string; reason?: string },
    unknown
  >
) {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    unknown,
    { eventId: string; reason?: string },
    unknown
  >(
    ({ eventId, reason }: { eventId: string; reason?: string }) =>
      api.post(
        `operation/administration/members/${memberId}/events/${eventId}/toggle-absence`,
        { reason }
      ),
    {
      ...config,
      onMutate: async ({ eventId }) => {
        const key = [
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "events",
        ]
        await queryClient.cancelQueries(key)
        const previous = queryClient.getQueryData<IMemberEvent[]>(key)
        queryClient.setQueryData(
          key,
          previous?.map((event) =>
            event.id === eventId
              ? { ...event, planned_absent: !event.planned_absent }
              : event
          )
        )

        return { previous }
      },
      onSettled(data, err, vars, conext) {
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "events",
        ])
      },
      onError(err, vars, context: any) {
        config?.onError?.(err, vars, context)
        queryClient.setQueryData(
          [
            "operation",
            "administration",
            "members",
            "portal",
            memberId,
            "events",
          ],
          context?.previous
        )
      },
    }
  )
}

export function useCalendarSubscriptions(
  memberId: string,
  params?: ICalenderParam,
  config?: UseQueryOptions<IEvent[]>
) {
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "events-calendar",
      params,
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useCalendarWaitingList(
  memberId: string,
  params?: ICalenderParam,
  config?: UseQueryOptions<IEvent[]>
) {
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "waiting-events-calendar",
      params,
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useCalendarPresubscriptions(
  memberId: string,
  params?: ICalenderParam,
  config?: UseQueryOptions<IPresubscription[]>
) {
  return useQuery<IPresubscription[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "presubscriptions-calendar",
      params,
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}
